import { ReactNode } from 'react';

export const PRIVACY_POLICY_TEXT: ReactNode = (
  <>
    <p className="mb-4 text-sm text-gray-600">
      Effective Date: April 29, 2025
      <br />
      Last Updated: November 25th, 2025
    </p>

    <p className="mb-6">
      This Privacy Policy describes how Lumena, Inc. ("Lumena INC," "we," "our," or "us") collects,
      uses, and protects the personal data of individuals ("you" or "users") who access and use our
      web application at https://app.lumenalabs.com (the "Cloud App"). This application is available
      only to authorized customers and participants in Lumena MindGym sessions.
    </p>

    <p className="mb-6">
      By accessing or using the Cloud App, you consent to the practices described in this Privacy
      Policy.
    </p>

    <hr className="my-6 border-gray-300" />

    <h2 className="mb-3 text-xl font-semibold">1. Scope and Applicability</h2>
    <p className="mb-6">
      This Privacy Policy applies solely to data collected through the Cloud App. If you interact
      with other Lumena Inc. websites or services, different privacy terms may apply.
    </p>

    <hr className="my-6 border-gray-300" />

    <h2 className="mb-3 text-xl font-semibold">2. Information We Collect</h2>
    <p className="mb-4">We may collect the following categories of information:</p>

    <h3 className="mb-2 text-lg font-medium">a. Personal Identifiers</h3>
    <ul className="mb-4 list-inside list-disc space-y-1">
      <li>Full name</li>
      <li>Phone number and/or email address (for authentication)</li>
      <li>Login credentials (passwords are stored securely using bcrypt hashing)</li>
    </ul>

    <h3 className="mb-2 text-lg font-medium">c. Biometric and Session Data</h3>
    <ul className="mb-4 list-inside list-disc space-y-1">
      <li>
        Data generated during your MindGym sessions, including but not limited to heart rate,
        respiration, stress markers, cognitive metrics, and related biometric signals.
      </li>
      <li>Session history and performance analytics</li>
    </ul>
    <p className="mb-6 italic">
      Note: Depending on the context and regulatory interpretation, some biometric data may
      constitute Protected Health Information (PHI) or sensitive personal data under applicable
      laws.
    </p>

    <h3 className="mb-2 text-lg font-medium">d. Device and Technical Data</h3>
    <ul className="mb-6 list-inside list-disc space-y-1">
      <li>IP address</li>
      <li>Device/browser type and version</li>
      <li>Access timestamps</li>
      <li>Security logs and session metadata</li>
    </ul>

    <hr className="my-6 border-gray-300" />

    <h2 className="mb-3 text-xl font-semibold">3. How We Use Your Data</h2>
    <p className="mb-4">We use your data to:</p>
    <ul className="mb-6 list-inside list-disc space-y-1">
      <li>Authenticate and authorize your access to the Cloud App</li>
      <li>Provide secure access to your personal MindGym session history and analytics</li>
      <li>Analyze usage to improve our offerings and performance insights</li>
      <li>
        Ensure system security and compliance with U.S. Department of Defense (DoD) and commercial
        aviation requirements
      </li>
      <li>Maintain audit logs for regulatory and contractual obligations</li>
    </ul>

    <hr className="my-6 border-gray-300" />

    <h2 className="mb-3 text-xl font-semibold">4. Legal Basis for Processing</h2>
    <p className="mb-4">We process your personal data based on:</p>
    <ul className="mb-6 list-inside list-disc space-y-1">
      <li>Performance of a contract — to provide services requested by you or your employer</li>
      <li>Legitimate interest — to improve our service and ensure security</li>
      <li>
        Legal obligations — to comply with applicable DoD standards, privacy laws, and security
        requirements
      </li>
    </ul>

    <hr className="my-6 border-gray-300" />

    <h2 className="mb-3 text-xl font-semibold">5. Data Security</h2>
    <p className="mb-4">
      We employ strong technical and organizational measures to safeguard your data, including:
    </p>
    <ul className="mb-6 list-inside list-disc space-y-1">
      <li>End-to-end encryption via TLS for all transmissions</li>
      <li>Bcrypt hashing for all stored passwords</li>
      <li>Role-based access control (RBAC)</li>
      <li>Enforced session expiration and token validation using secure JWT handling</li>
      <li>
        Hosting on AWS infrastructure hardened according to NIST and DoD standards, and secured
        behind Cloudflare CDN and WAF
      </li>
    </ul>

    <hr className="my-6 border-gray-300" />

    <h2 className="mb-3 text-xl font-semibold">6. Data Retention</h2>
    <p className="mb-6">
      Personal and biometric data is retained for the duration of your relationship with Lumena and
      as necessary for contractual and compliance purposes.
    </p>
    <p className="mb-6">
      Inactive accounts may be archived or deleted after 24 months of inactivity, unless longer
      retention is required by federal or military data retention standards.
    </p>

    <hr className="my-6 border-gray-300" />

    <h2 className="mb-3 text-xl font-semibold">7. Third Parties and Data Sharing</h2>
    <p className="mb-4">We do not sell or rent personal data. We may share limited data with:</p>
    <ul className="mb-6 list-inside list-disc space-y-1">
      <li>Cloud and infrastructure providers: AWS, Cloudflare (US-based)</li>
      <li>
        Security, compliance, and monitoring partners: As required for system operations and
        auditing
      </li>
      <li>
        DoD-authorized contractors or evaluators: If required under government agreements or audits
      </li>
    </ul>
    <p className="mb-6">Data is never used for advertising, model training, or profiling.</p>

    <hr className="my-6 border-gray-300" />

    <h2 className="mb-3 text-xl font-semibold">8. Cookies and Tracking</h2>
    <p className="mb-6">
      The Cloud App does not currently use third-party analytics or tracking cookies. In the future,
      we may enable Google Analytics or similar services strictly for internal usage analysis. This
      policy will be updated accordingly before activation.
    </p>

    <hr className="my-6 border-gray-300" />

    <h2 className="mb-3 text-xl font-semibold">9. Your Rights and Choices</h2>
    <p className="mb-4">You may:</p>
    <ul className="mb-6 list-inside list-disc space-y-1">
      <li>Request access to your personal data</li>
      <li>Request correction of inaccurate data</li>
      <li>Request deletion of your data (subject to contractual or regulatory limitations)</li>
      <li>Request a copy of your data in portable format</li>
    </ul>
    <p className="mb-4">Requests can be submitted to: privacy@lumenalabs.com</p>
    <p className="mb-6">
      We will respond to all valid requests within 30 days, or as required under applicable law or
      military contract obligations.
    </p>

    <hr className="my-6 border-gray-300" />

    <h2 className="mb-3 text-xl font-semibold">10. Data Residency and International Transfers</h2>
    <p className="mb-6">
      All data is stored and processed exclusively in the United States. We do not transfer data
      internationally.
    </p>

    <hr className="my-6 border-gray-300" />

    <h2 className="mb-3 text-xl font-semibold">11. Changes to This Privacy Policy</h2>
    <p className="mb-6">
      We may update this policy from time to time to reflect changes in legal, technical, or
      business practices. Updates will be posted to this page with a new "Effective Date." Continued
      use of the Cloud App after such updates constitutes consent to the revised policy.
    </p>

    <hr className="my-6 border-gray-300" />

    <h2 className="mb-3 text-xl font-semibold">12. Contact Us</h2>
    <p className="mb-4">For any questions, concerns, or requests regarding this Privacy Policy:</p>
    <div className="mb-6">
      <p>
        <strong>Lumena Inc.</strong>
      </p>
      <p>Email: privacy@lumenalabs.com</p>
      <p>Address: Lumena Inc., 5180 Fox Street, Denver, CO 80216</p>
    </div>
  </>
);

export const TERMS_OF_SERVICE_TEXT: ReactNode = (
  <>
    <p className="mb-4 text-sm text-gray-600">
      Effective Date: April 29, 2025
      <br />
      Last Updated: November 25th, 2025
    </p>

    <p className="mb-6">
      These Terms of Service ("Terms") govern your access to and use of the Cloud App (the "App"), a
      secure, customer-only web application operated by Lumena Inc. ("Lumena," "we," "us," or
      "our"), located at https://app.lumenalabs.com. By accessing or using the App, you agree to be
      bound by these Terms and our Privacy Policy.
    </p>

    <p className="mb-6">If you do not agree to these Terms, do not access or use the App.</p>

    <hr className="my-6 border-gray-300" />

    <h2 className="mb-3 text-xl font-semibold">1. Eligibility and Access</h2>
    <p className="mb-4">
      The Cloud App is restricted to authorized Lumena customers and their designated users. You may
      only access the App if:
    </p>
    <ul className="mb-6 list-inside list-disc space-y-1">
      <li>You are an active participant in Lumena's services (e.g., MindGym sessions);</li>
      <li>You have valid login credentials issued by Lumena.</li>
    </ul>

    <hr className="my-6 border-gray-300" />

    <h2 className="mb-3 text-xl font-semibold">2. Account Security</h2>
    <p className="mb-6">
      You are responsible for maintaining the confidentiality of your login credentials. You agree
      not to share your password and to notify us immediately at support@lumenalabs.com if you
      suspect unauthorized use of your account.
    </p>
    <p className="mb-6">
      You must use a strong password and are responsible for all activities conducted under your
      account.
    </p>

    <hr className="my-6 border-gray-300" />

    <h2 className="mb-3 text-xl font-semibold">3. Acceptable Use</h2>
    <p className="mb-4">You agree not to:</p>
    <ul className="mb-6 list-inside list-disc space-y-1">
      <li>Access or attempt to access data you are not authorized to view;</li>
      <li>Use the App to upload, transmit, or distribute any malicious code or content;</li>
      <li>Reverse engineer, probe, or test the App for vulnerabilities;</li>
      <li>Use automated tools (e.g., bots, scrapers) unless expressly authorized;</li>
      <li>
        Use the App in any way that violates applicable laws or regulations, including U.S. military
        and aviation standards.
      </li>
    </ul>

    <hr className="my-6 border-gray-300" />

    <h2 className="mb-3 text-xl font-semibold">4. Data and Privacy</h2>
    <p className="mb-6">
      By using the App, you acknowledge that Lumena will collect and process certain personal and
      biometric data, including data from your in-person MindGym sessions. All data processing is
      governed by our Privacy Policy, which is incorporated by reference into these Terms.
    </p>

    <hr className="my-6 border-gray-300" />

    <h2 className="mb-3 text-xl font-semibold">5. Intellectual Property</h2>
    <p className="mb-6">
      All content and software associated with the App, including but not limited to text, images,
      analytics, and algorithms, are the property of Lumena or its licensors. You may not copy,
      modify, distribute, or create derivative works of any part of the App without our prior
      written consent.
    </p>

    <hr className="my-6 border-gray-300" />

    <h2 className="mb-3 text-xl font-semibold">6. System Integrity and Availability</h2>
    <p className="mb-6">
      We strive to maintain secure and reliable access to the Cloud App. However, we do not
      guarantee that the App will be available at all times. Maintenance windows, outages, or
      interruptions may occur. We are not liable for any loss of access, data, or functionality
      during such events.
    </p>

    <hr className="my-6 border-gray-300" />

    <h2 className="mb-3 text-xl font-semibold">7. Termination</h2>
    <p className="mb-6">
      We reserve the right to suspend or terminate your access to the App, without notice or
      liability, if we reasonably believe you have violated these Terms, misused the system, or pose
      a risk to the security or privacy of other users.
    </p>

    <hr className="my-6 border-gray-300" />

    <h2 className="mb-3 text-xl font-semibold">8. Disclaimer of Warranties</h2>
    <p className="mb-6">
      The Cloud App is provided "as is" and "as available." To the maximum extent permitted by law,
      Lumena disclaims all warranties, express or implied, including fitness for a particular
      purpose, merchantability, and non-infringement.
    </p>

    <hr className="my-6 border-gray-300" />

    <h2 className="mb-3 text-xl font-semibold">9. Limitation of Liability</h2>
    <p className="mb-6">
      To the fullest extent permitted by law, Lumena and its affiliates shall not be liable for any
      indirect, incidental, special, or consequential damages arising out of or related to your use
      of the App.
    </p>
    <p className="mb-6">
      Our total cumulative liability under these Terms shall not exceed one hundred U.S. dollars
      ($100).
    </p>

    <hr className="my-6 border-gray-300" />

    <h2 className="mb-3 text-xl font-semibold">10. Governing Law and Jurisdiction</h2>
    <p className="mb-6">
      These Terms shall be governed by and construed in accordance with the laws of the State of
      Colorado, without regard to its conflict of law principles. Any legal action or proceeding
      arising under these Terms shall be brought exclusively in the state or federal courts located
      in Colorado.
    </p>

    <hr className="my-6 border-gray-300" />

    <h2 className="mb-3 text-xl font-semibold">11. Compliance with Government Standards</h2>
    <p className="mb-6">
      If you are accessing the App on behalf of the United States Government or a military branch,
      you agree that your use complies with applicable DoD cybersecurity, privacy, and operational
      security (OPSEC) standards.
    </p>

    <hr className="my-6 border-gray-300" />

    <h2 className="mb-3 text-xl font-semibold">12. Modifications to These Terms</h2>
    <p className="mb-6">
      We may update these Terms at any time. Updated Terms will be posted on this page with a new
      effective date. Continued use of the App after such changes constitutes acceptance of the
      revised Terms.
    </p>

    <hr className="my-6 border-gray-300" />

    <h2 className="mb-3 text-xl font-semibold">13. Contact Information</h2>
    <p className="mb-4">For support, questions, or legal inquiries, please contact:</p>
    <div className="mb-6">
      <p>
        <strong>Lumena Labs, Inc.</strong>
      </p>
      <p>✉️ support@lumenalabs.com</p>
      <p>Lumena Inc., 5180 Fox Street, Denver, CO 80216</p>
    </div>
  </>
);
